import { IAppleLoginCredentials } from '../types/auth';

interface AppleSignInResponse {
  authorization: { id_token: string; state: string; code?: string };
  user?: {
    email?: string;
    name?: { firstName?: string; lastName?: string };
  };
}

interface AppleSignInSDK {
  auth: {
    init: (config: {
      clientId: string;
      redirectURI: string;
      scope: string;
      state: string;
      usePopup: boolean;
    }) => void;
    signIn: () => Promise<AppleSignInResponse>;
  };
}

declare global {
  interface Window {
    AppleID?: AppleSignInSDK;
  }
}

let sdkPromise: Promise<void> | undefined;

// Preload before a click: awaiting a download inside the click handler can
// lose user activation and cause mobile browsers to block the popup.
export function loadAppleSignIn(): Promise<void> {
  if (window.AppleID) return Promise.resolve();
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    const finish = (error?: Error) => {
      window.clearTimeout(timeout);
      script.onload = null;
      script.onerror = null;
      if (error) {
        script.remove();
        sdkPromise = undefined;
        reject(error);
      } else {
        resolve();
      }
    };
    const timeout = window.setTimeout(() => {
      finish(new Error('Apple sign-in could not load. Please try again.'));
    }, 15000);
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => finish(window.AppleID ? undefined : new Error('Apple sign-in is unavailable. Please try again.'));
    script.onerror = () => finish(new Error('Apple sign-in could not load. Please check your connection and try again.'));
    document.head.appendChild(script);
  });
  return sdkPromise;
}

export async function signInWithApple(): Promise<IAppleLoginCredentials> {
  const clientId = process.env.REACT_APP_APPLE_CLIENT_ID;
  const redirectURI = process.env.REACT_APP_APPLE_REDIRECT_URI;
  if (!clientId || !redirectURI) {
    throw new Error('Apple sign-in is not configured yet. Please use Google or email to continue.');
  }
  if (!window.AppleID) {
    // Retry loading, but require another click to preserve the user gesture.
    void loadAppleSignIn().catch(() => undefined);
    throw new Error('Apple sign-in is loading. Please try again in a moment.');
  }

  const state = Array.from(window.crypto.getRandomValues(new Uint8Array(32)))
    .map(value => value.toString(16).padStart(2, '0')).join('');
  window.AppleID.auth.init({
    clientId,
    redirectURI,
    scope: 'name email',
    state,
    usePopup: true,
  });
  const response = await window.AppleID.auth.signIn();
  if (response.authorization?.state !== state || !response.authorization?.id_token) {
    throw new Error('Unable to verify the Apple sign-in response. Please try again.');
  }

  return {
    identityToken: response.authorization.id_token,
    authorizationCode: response.authorization.code,
    email: response.user?.email,
    fullName: response.user?.name ? {
      givenName: response.user.name.firstName,
      familyName: response.user.name.lastName,
    } : undefined,
  };
}

export function isAppleSignInCancelled(error: unknown): boolean {
  const code = (error as { error?: string } | null)?.error;
  return code === 'popup_closed_by_user' || code === 'user_cancelled_authorize';
}
